'use client'
import { useMotionValue } from 'framer-motion'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useRef, useMemo, useState } from 'react'

import Shared from '@/shared'
import useHash from '@/shared/hooks/useHash'
import useHashChange from '@/shared/hooks/useHashChange'
import type { MotionValue } from 'framer-motion'

import { SHEET_STATE_TYPE } from '@/enums/detail'
import { HASH_KEY_TYPE } from '@/enums/map'

interface DetailClientViewModel {
  states: {
    isRoadViewOpen: boolean
    sheetType: SHEET_STATE_TYPE
    parkinglotRef: React.MutableRefObject<null | HTMLDivElement>
    sheetPositionY: MotionValue<number>
    isNotFoundPinsModalOpen: boolean
  }
  setters: {
    setIsRoadViewOpen: React.Dispatch<React.SetStateAction<boolean>>
  }
  handlers: {
    onCloseModal: () => void
  }
}

interface UseDetailClientViewModelProps {
  type: string
  id: string
}

const useDetailClientViewModel = ({ type, id }: UseDetailClientViewModelProps): DetailClientViewModel => {
  const onChangeSheetHash = useHashChange(HASH_KEY_TYPE.SHEET)
  const router = useRouter()
  const pathname = usePathname()
  const { getHashByKey, updateHash } = useHash()
  const [isRoadViewOpen, setIsRoadViewOpen] = useState(false)
  const [isNotFoundPinsModalOpen, setIsNotFoundPinsModalOpen] = useState(false)
  const isPinsUpdateOnce = useRef(false)
  const [viewHeight, setViewHeight] = useState(window.innerHeight)
  const parkinglotRef = useRef<null | HTMLDivElement>(null)
  const setMapLayoutPositions = Shared.States.useMapLayoutPositions((state) => state.setBottomSheetYPosition)
  const resetPurchase = Shared.States.useIsPurchase((state) => state.resetIsPurchase)
  const globalCachedPins = Shared.States.useGlobalCachedPins((state) => state.cachedPins)
  const sheetPositionY = useMotionValue(0)
  const isOnlyDetailView = pathname.startsWith('/p')
  const searchParams = new URLSearchParams(window.location.search)

  useEffect(() => {
    if (!isPinsUpdateOnce.current && Object.values(globalCachedPins).flat().length && searchParams.get('id')) {
      isPinsUpdateOnce.current = true
      const mapPins = Object.values(globalCachedPins).flatMap((i) => i.pins)
      const isNotFoundInPins = !mapPins.some((pin) => pin.seq === Number(searchParams.get('id')))
      if (mapPins.length && isNotFoundInPins) {
        setIsNotFoundPinsModalOpen(true)
        const pathname = window.location.pathname
        const searchParams = window.location.search
        const hash = updateHash({ sheet: SHEET_STATE_TYPE.CLOSE })
        router.replace(`${pathname}${searchParams}${hash}`)
      }
    }
  }, [globalCachedPins])

  const sheetType = useMemo<SHEET_STATE_TYPE>(() => {
    const sheetState = getHashByKey(HASH_KEY_TYPE.SHEET) as SHEET_STATE_TYPE

    if (viewHeight < 250 || (isOnlyDetailView && !sheetState)) {
      return SHEET_STATE_TYPE.VISIBLE
    }
    if (!sheetState) {
      if (searchParams.get('type') && searchParams.get('id')) {
        return SHEET_STATE_TYPE.HIDDEN
      }
      return SHEET_STATE_TYPE.CLOSE
    }
    return Object.values(SHEET_STATE_TYPE).find((type) => type === sheetState) ?? SHEET_STATE_TYPE.HIDDEN
  }, [onChangeSheetHash, viewHeight])

  useEffect(() => {
    resetPurchase()
  }, [id])

  useEffect(() => {
    setViewHeight(window.innerHeight)
    const handleResize = () => {
      setViewHeight(window.innerHeight)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // TODO: type 의존성 추후에 제거, P와 S가 동일한 높이를 가지게 될 것
  useEffect(() => {
    let bottomSheetEl: null | HTMLDivElement = null
    if (parkinglotRef.current == null) return
    bottomSheetEl = parkinglotRef.current
    const y = bottomSheetEl.getBoundingClientRect().y
    setMapLayoutPositions(y)
  }, [onChangeSheetHash, type])

  useEffect(() => {
    const unsubscribeY = sheetPositionY.on('change', () => {
      if (parkinglotRef.current == null) return
      const y = parkinglotRef.current.getBoundingClientRect().y
      setMapLayoutPositions(y)
    })

    return () => unsubscribeY()
  }, [sheetPositionY])

  const onCloseModal = () => {
    setIsNotFoundPinsModalOpen(false)
  }

  return {
    states: {
      isRoadViewOpen,
      sheetType,
      parkinglotRef,
      sheetPositionY,
      isNotFoundPinsModalOpen
    },
    setters: {
      setIsRoadViewOpen
    },
    handlers: {
      onCloseModal
    }
  }
}

export default useDetailClientViewModel
